/**
 * AI 跟单法币兑换确认弹窗
 * 样式沿用合并前 aicopy 专用 bottom sheet（双卡片 + Cancel/Confirm），
 * 不与 Convert 页新版 ConvertConfirmSheet 共用。
 */
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Hooks } from "@xt/xtbase";
const { useTranslation } = Hooks;
import { isFiatMarketCode } from "@/utils/fiatMarket";
import FiatCurrencyIcon from "@/components/Pages/Convert/FiatCurrencyIcon";
import styles from "./index.module.less";

function formatApiAmount(str, currency) {
  if (str == null || str === "") return "";
  const s = String(str).replace(/,/g, "").trim();
  if (!s || !/^-?\d+(\.\d+)?$/.test(s)) return "";
  const isFiat = currency && isFiatMarketCode(currency);
  if (isFiat) {
    const n = parseFloat(s);
    if (!Number.isFinite(n)) return "";
    return n.toFixed(2).replace(/\.?0+$/, "") || "0";
  }
  return s.replace(/\.?0+$/, "") || "0";
}

function formatWithSameDecimals(num, referenceStr) {
  if (!Number.isFinite(num)) return "";
  const ref = referenceStr != null ? String(referenceStr) : "";
  const dec = ref.includes(".") ? (ref.split(".")[1] || "").length : 0;
  const s = num.toFixed(dec).replace(/\.?0+$/, "");
  return s || "0";
}

function formatTo5Decimals(value) {
  if (value == null || value === "") return "";
  const s = String(value).replace(/,/g, "").trim();
  const n = parseFloat(s);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(5).replace(/\.?0+$/, "") || "0";
}

export default function ConvertConfirmModal({
  open,
  onCancel,
  sourceCurrency,
  sourceAmount,
  previewData,
  previewLoading,
  onConfirm,
  confirmDisabled,
  /** @default {'fiatToUsdt'} */
  direction = "fiatToUsdt",
  /** USDT→法币时到账法币代码 */
  receiveCurrency,
}) {
  const t = useTranslation();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!open || !mounted || typeof document === "undefined") return null;

  const isUsdtToFiat = direction === "usdtToFiat";
  const spendCode = (sourceCurrency || "").toUpperCase();
  const receiveCode = isUsdtToFiat
    ? (previewData?.targetCurrency || receiveCurrency || "USD").toUpperCase()
    : (previewData?.targetCurrency || "USDT").toUpperCase();

  const renderPriceRows = () => {
    if (previewLoading) {
      return (
        <div className={styles.convertConfirmItem}>
          <span className={styles.convertConfirmItemTitle}>{t("convert.loading")}...</span>
        </div>
      );
    }
    if (!previewData) {
      return (
        <>
          <div className={styles.convertConfirmItem}>
            <span className={styles.convertConfirmItemTitle}>{t("convert.price")}</span>
            <span className={styles.convertConfirmItemValue}>—</span>
          </div>
          {/* <div className={styles.convertConfirmItem}>
            <span className={styles.convertConfirmItemTitle}>{t("convert.inversePrice")}</span>
            <span className={styles.convertConfirmItemValue}>—</span>
          </div> */}
          <div className={styles.convertConfirmItem}>
            <span className={styles.convertConfirmItemTitle}>{t("convert.fee")}</span>
            <span className={styles.convertConfirmItemValue}>—</span>
          </div>
          <div className={styles.convertConfirmItem}>
            <span className={styles.convertConfirmItemTitle}>{t("convert.youWillReceive")}</span>
            <span className={styles.convertConfirmItemValue}>—</span>
          </div>
        </>
      );
    }

    const feeCur = (previewData.feeCurrencyCode || receiveCode || "USDT").toUpperCase();
    const feeDisplay = previewData.totalFee
      ? `${formatApiAmount(previewData.totalFee, feeCur)} ${feeCur}`
      : "—";

    if (isUsdtToFiat) {
      const fiatCode = receiveCode;
      const rateNum = parseFloat(previewData.exchangeRate) || 0;
      return (
        <>
          <div className={styles.convertConfirmItem}>
            <span className={styles.convertConfirmItemTitle}>{t("convert.price")}</span>
            <span className={styles.convertConfirmItemValue}>
              1 USDT ≈ {rateNum > 0 ? formatTo5Decimals(previewData.exchangeRate) : "—"} {fiatCode}
            </span>
          </div>
          {/* <div className={styles.convertConfirmItem}>
            <span className={styles.convertConfirmItemTitle}>{t("convert.inversePrice")}</span>
            <span className={styles.convertConfirmItemValue}>
              1 {fiatCode} = {rateNum > 0 ? formatWithSameDecimals(1 / rateNum, previewData.exchangeRate) : "—"} USDT
            </span>
          </div> */}
          <div className={styles.convertConfirmItem}>
            <span className={styles.convertConfirmItemTitle}>{t("convert.fee")}</span>
            <span className={styles.convertConfirmItemValue}>{feeDisplay}</span>
          </div>
          <div className={styles.convertConfirmItem}>
            <span className={styles.convertConfirmItemTitle}>{t("convert.youWillReceive")}</span>
            <span className={styles.convertConfirmItemValue} title={`${formatTo5Decimals(previewData.targetAmount)} ${fiatCode}`}>
              {formatTo5Decimals(previewData.targetAmount)} {fiatCode}
            </span>
          </div>
        </>
      );
    }

    const rateNum = parseFloat(previewData.exchangeRate) || 0;
    return (
      <>
        <div className={styles.convertConfirmItem}>
          <span className={styles.convertConfirmItemTitle}>{t("convert.price")}</span>
          <span className={styles.convertConfirmItemValue}>
            1 {spendCode} ≈ {rateNum > 0 ? formatTo5Decimals(1 / rateNum) : "—"} {receiveCode}
          </span>
        </div>
        {/* <div className={styles.convertConfirmItem}>
          <span className={styles.convertConfirmItemTitle}>{t("convert.inversePrice")}</span>
          <span className={styles.convertConfirmItemValue}>
            1 {receiveCode} = {rateNum > 0 ? formatApiAmount(previewData.exchangeRate, spendCode) : "—"} {spendCode}
          </span>
        </div> */}
        <div className={styles.convertConfirmItem}>
          <span className={styles.convertConfirmItemTitle}>{t("convert.fee")}</span>
          <span className={styles.convertConfirmItemValue}>{feeDisplay}</span>
        </div>
        <div className={styles.convertConfirmItem}>
          <span className={styles.convertConfirmItemTitle}>{t("convert.youWillReceive")}</span>
          <span className={styles.convertConfirmItemValue} title={`${formatTo5Decimals(previewData.targetAmount)} ${receiveCode}`}>
            {formatTo5Decimals(previewData.targetAmount)} {receiveCode}
          </span>
        </div>
      </>
    );
  };

  return createPortal(
    <div className={styles.convertConfirmOverlay} onClick={onCancel}>
      <div className={styles.convertConfirmSheet} onClick={(e) => e.stopPropagation()}>
        <div className={styles.convertConfirmHeader}>
          <h3 className={styles.convertConfirmTitle}>{t("convert.confirmOrder")}</h3>
          <button type="button" className={styles.convertConfirmClose} onClick={onCancel} aria-label={t("convert.close")}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        <div className={styles.convertConfirmBody}>
          <div className={styles.convertConfirmCards}>
            <div className={styles.convertConfirmCurrencyCard}>
              <FiatCurrencyIcon code={spendCode} className={styles.convertConfirmCurrencyIcon} style={{ background: "#26A69A" }} />
              <div className={styles.convertConfirmCurrencyText}>
                <span className={styles.convertConfirmLabel}>{t("convert.spend")}</span>
                <span className={styles.convertConfirmValue}>
                  {sourceAmount || "0"} {spendCode}
                </span>
              </div>
            </div>
            <span className={styles.convertConfirmArrow} aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10.1348 14.4209L13.5137 11.042L2.49902 11.042L2.49902 8.95801L13.5127 8.95801L10.1348 5.58105L11.6074 4.10742L17.5 10L17.5 10.001L11.6074 15.8936L10.1348 14.4209Z" fill="#707A8A" />
              </svg>
            </span>
            <div className={styles.convertConfirmCurrencyCard}>
              <FiatCurrencyIcon code={receiveCode} className={styles.convertConfirmCurrencyIcon} style={{ background: "#26A69A" }} />
              <div className={styles.convertConfirmCurrencyText}>
                <span className={styles.convertConfirmLabel}>{t("convert.receive")}</span>
                <span
                  className={styles.convertConfirmValue}
                  title={
                    previewLoading
                      ? ""
                      : previewData
                        ? `${formatTo5Decimals(previewData.targetAmount)} ${receiveCode}`
                        : "—"
                  }
                >
                  {previewLoading
                    ? "..."
                    : previewData
                      ? formatTo5Decimals(previewData.targetAmount)
                      : "—"}
                  {!previewLoading ? ` ${receiveCode}` : null}
                </span>
              </div>
            </div>
          </div>
          <div className={styles.convertConfirmDivider} />
          <div className={styles.convertConfirmList}>{renderPriceRows()}</div>
          <div className={styles.convertConfirmFooter}>
            <div className={styles.convertConfirmActions}>
              <button type="button" className={styles.convertConfirmBtnCancel} onClick={onCancel}>
                {t("convert.cancel")}
              </button>
              <button
                type="button"
                className={styles.convertConfirmBtnConfirm}
                onClick={onConfirm}
                disabled={confirmDisabled}
              >
                {t("convert.confirm")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
